"use client";
import { useNavigate, useParams } from "react-router-dom";
import {
  useFetchInstalledApplicationDetails,
  useInstallApplicationTestConnectionMutation,
  useFetchChannelDetails,
  useFetchNaqelCredentials,
  useChannelConnectionMutation,
  useFetchJtCredentials,
} from "../../../apis/integrationApis";
import Input from "../../../components/Input";
import toast from "react-hot-toast";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";

const NAQEL_FIELDS = [
  { key: "userName", label: "Username", type: "text", placeholder: "Enter username" },
  { key: "password", label: "Password", type: "password", placeholder: "Leave blank to keep existing" },
  { key: "customerCode", label: "Customer Code", type: "text", placeholder: "e.g. NL123456" },
  { key: "branchCode", label: "Branch Code", type: "text", placeholder: "e.g. NL567899" },
];

const JT_FIELDS = [
  { key: "customerCode", label: "Customer Code", type: "text", placeholder: "Enter customer code" },
  { key: "apiAccount", label: "API Account", type: "text", placeholder: "Enter API account" },
  { key: "privateKey", label: "Private Key", type: "password", placeholder: "Leave blank to keep existing" },
];


const SubmitButton = ({
  isPending,
  isConnected,
  label = "Save & Connect",
}: {
  isPending: boolean;
  isConnected: boolean;
  label?: string;
}) => (
  <div className="col-span-6 flex justify-end">
    <button
      type="submit"
      disabled={isPending || isConnected}
      className={`px-6 py-2.5 flex items-center gap-2 rounded-lg font-semibold shadow transition
        ${isConnected
          ? "bg-green-500 text-white cursor-default"
          : "bg-gradient-to-r from-yellow-400 to-yellow-500 text-black hover:from-yellow-500 hover:to-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed"
        }`}
    >
      {isPending && !isConnected && (
        <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
      )}
      {isConnected ? "✅ Connected" : isPending ? "Saving..." : label}
    </button>
  </div>
);


const DefaultForm = ({
  data,
  isPending,
  isConnected,
  onSubmit,
}: {
  data: any;
  isPending: boolean;
  isConnected: boolean;
  onSubmit: (e: any) => void;
}) => (
  <form className="grid grid-cols-6 gap-6 p-6" onSubmit={onSubmit}>
    <Input
      className="col-span-6"
      title="Store Name"
      name="storeName"
      placeholder="Enter Name"
      value={data?.companyName}
    />
    <Input
      className="col-span-6"
      title="Store URL"
      name="storeUrl"
      placeholder="Enter URL"
      value={data?.webhookUrl}
    />
    <Input
      className="col-span-6"
      name="accessToken"
      title="API Access Token"
      placeholder="Enter Access Token"
      value={data?.secretKey}
    />
    <SubmitButton isPending={isPending} isConnected={isConnected} label="Test Connection" />
  </form>
);


const NaqelForm = ({
  savedCreds,
  isPending,
  isConnected,
  onSubmit,
}: {
  savedCreds: any;
  isPending: boolean;
  isConnected: boolean;
  onSubmit: (data: any) => void;
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Record<string, string>>({ mode: "onChange" });

  useEffect(() => {
    if (savedCreds) {
      reset({
        userName: savedCreds.userName ?? "",
        password: "",
        customerCode: savedCreds.customerCode ?? "",
        branchCode: savedCreds.branchCode ?? "",
      });
    }
  }, [savedCreds, reset]);

  return (
    <form className="grid grid-cols-6 gap-6 p-6" onSubmit={handleSubmit(onSubmit)}>
      {NAQEL_FIELDS.map((field) => (
        <div key={field.key} className="col-span-6 flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            {field.label}
            {field.key !== "password" && (
              <span className="ml-1 text-xs text-gray-400 font-normal">(autofilled)</span>
            )}
          </label>
          <input
            type={field.type}
            placeholder={field.placeholder}
            {...register(field.key, {
              required:
                field.key === "password"
                  ? false
                  : `${field.label} is required`,
            })}
            className="px-4 py-3 text-sm w-full rounded-full outline-none bg-white border border-[#D8D8D8] focus:border-blue-500 transition"
          />
          {errors[field.key] && (
            <span className="text-red-500 text-xs mt-0.5">
              {errors[field.key]?.message as string}
            </span>
          )}
        </div>
      ))}
      <SubmitButton isPending={isPending} isConnected={isConnected} label="Save & Reconnect" />
    </form>
  );
};

const JtForm = ({
  savedCreds,
  isPending,
  isConnected,
  onSubmit,
}: {
  savedCreds: any;
  isPending: boolean;
  isConnected: boolean;
  onSubmit: (data: any) => void;
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Record<string, string>>({ mode: "onChange" });

  useEffect(() => {
    if (savedCreds) {
      reset({
        customerCode: savedCreds.customerCode ?? "",
        apiAccount: savedCreds.apiAccount ?? "",
        privateKey: "",
      });
    }
  }, [savedCreds, reset]);

  return (
    <form className="grid grid-cols-6 gap-6 p-6" onSubmit={handleSubmit(onSubmit)}>
      {JT_FIELDS.map((field) => (
        <div key={field.key} className="col-span-6 flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            {field.label}
            {(field.key !== "privateKey" && field.key !== "password") && (
              <span className="ml-1 text-xs text-gray-400 font-normal">(autofilled)</span>
            )}
          </label>
          <input
            type={field.type}
            placeholder={field.placeholder}
            {...register(field.key, {
              required:
                (field.key === "privateKey" || field.key === "password")
                  ? false
                  : `${field.label} is required`,
            })}
            className="px-4 py-3 text-sm w-full rounded-full outline-none bg-white border border-[#D8D8D8] focus:border-blue-500 transition"
          />
          {errors[field.key] && (
            <span className="text-red-500 text-xs mt-0.5">
              {errors[field.key]?.message as string}
            </span>
          )}
        </div>
      ))}
      <SubmitButton isPending={isPending} isConnected={isConnected} label="Save & Reconnect" />
    </form>
  );
};

// ─── Main EditModal ───────────────────────────────────────────────────────────
const EditModal = () => {
  const { id, insalledId } = useParams<{ id: string; insalledId: string }>();
  const navigate = useNavigate();

  const { data: channelData, isLoading: isChannelLoading } = useFetchChannelDetails(id ?? "");

  const companyName: string = isChannelLoading ? "" : (channelData?.companyName ?? "");
  const companyNameLower = companyName.toLowerCase();


  const isNaqel = companyNameLower.includes("naqel");
  const isAramex = companyNameLower.includes("aramex");
  const isJt = companyNameLower.includes("j&t") || companyNameLower.includes("jt express");

  const { data: installedData } = useFetchInstalledApplicationDetails(insalledId ?? "");

  const { data: naqelCreds, isLoading: isCredsLoading } =
    useFetchNaqelCredentials(companyName);

  const { data: jtCreds, isLoading: isJtCredsLoading } =
    useFetchJtCredentials(companyName);

  const { mutate: testMutate, isPending: isTestPending } =
    useInstallApplicationTestConnectionMutation(insalledId ?? "");

  const { mutate: naqelMutate, isPending: isNaqelPending } =
    useChannelConnectionMutation(
      channelData?.requestedId ?? "",
      companyName,
      channelData?.installedBy ?? ""
    );

  const [isConnected, setIsConnected] = useState(false);
  const isPending = (isNaqel || isJt) ? isNaqelPending : isTestPending;

  // ── Non-Naqel handler ──────────────────────────────────────────────────────
  const handleTestConnection = (event: any) => {
    event.preventDefault();
    testMutate(
      { applicationId: id, secretKey: installedData?.secretKey },
      {
        onSuccess: () => {
          toast.success("Connection tested successfully!");
          setIsConnected(true);
          navigate("/integration");
        },
        onError: (error: any) => {
          toast.error(error || "Test connection failed!");
          setIsConnected(false);
        },
      }
    );
  };

  // ── Naqel save & reconnect handler ────────────────────────────────────────
  const handleNaqelSave = (formData: Record<string, string>) => {
    const credentials: Record<string, string> = {
      userName: formData.userName,
      customerCode: formData.customerCode,
      branchCode: formData.branchCode,
      password: formData.password || naqelCreds?.password || "",
    };

    naqelMutate(
      { credentials, connectionId: insalledId },
      {
        onSuccess: () => {
          toast.success("Naqel credentials updated & reconnected!");
          setIsConnected(true);
          navigate("/integration");
        },
        onError: (error: any) => {
          toast.error(error || "Failed to update Naqel credentials!");
          setIsConnected(false);
        },
      }
    );
  };

  // ── J&T save & reconnect handler ────────────────────────────────────────
  const handleJtSave = (formData: Record<string, string>) => {
    const credentials: Record<string, string> = {
      customerCode: formData.customerCode,
      apiAccount: formData.apiAccount,
      privateKey: formData.privateKey || jtCreds?.privateKey || "",
    };

    naqelMutate(
      { credentials, connectionId: insalledId },
      {
        onSuccess: () => {
          toast.success("J&T credentials updated & reconnected!");
          setIsConnected(true);
          navigate("/integration");
        },
        onError: (error: any) => {
          toast.error(error || "Failed to update J&T credentials!");
          setIsConnected(false);
        },
      }
    );
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-lg border border-gray-100">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
        {channelData?.companyLogo && (
          <img
            src={channelData.companyLogo}
            alt={companyName}
            className="w-9 h-9 rounded object-contain border border-gray-200"
          />
        )}
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            {companyName || "Edit Integration"}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {isNaqel
              ? "Update your Naqel credentials. Leave password blank to keep existing."
              : isJt
                ? "Update your J&T credentials. Leave private key / password blank to keep existing."
                : "Update your application details and test the connection."}
          </p>
        </div>
      </div>

      {/* Body */}
      {isChannelLoading ? (
        <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
          Loading integration details...
        </div>
      ) : isNaqel ? (
        isCredsLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
            Loading credentials...
          </div>
        ) : (
          <NaqelForm
            savedCreds={naqelCreds}
            isPending={isPending}
            isConnected={isConnected}
            onSubmit={handleNaqelSave}
          />
        )
      ) : isJt ? (
        isJtCredsLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
            Loading J&T credentials...
          </div>
        ) : (
          <JtForm
            savedCreds={jtCreds}
            isPending={isPending}
            isConnected={isConnected}
            onSubmit={handleJtSave}
          />
        )
      ) : (
        <DefaultForm
          data={installedData}
          isPending={isPending}
          isConnected={isConnected}
          onSubmit={handleTestConnection}
        />
      )}
    </div>
  );
};

export default EditModal;